/**
 * Image Component Registry — satori-compatible renderers for OG images
 *
 * Maps ddCatalog component names to image-friendly HTML/CSS variants for satori.
 * These render as plain HTML divs with inline styles (satori's requirement:
 * flexbox only, no CSS Grid, no Tailwind).
 *
 * Used by the generate-og-image edge function via @json-render/image's
 * renderToSvg/renderToPng with this registry.
 *
 * Components:
 *   DramaScore          -> Styled score display (number + circle + color)
 *   DramaCategoryBadge  -> Badge with category text + color
 *   NarrativeBlock      -> Prose text block with emphasis styling
 *   TierBadge           -> Tier indicator badge
 *   InsightRow          -> Key insight summary line
 *
 * Task: [#1158.P1.4] OG Image Registry
 * Agent: Diego <diego@liftping.net>
 */

import React from "react";
import { CATEGORY_COLORS, CATEGORY_LABELS } from "@dramadetector/shared/og";

/** Local type definition — @json-render/image is not available as a dependency */
type ComponentRegistry = Record<
	string,
	React.ComponentType<{
		element: { props: Record<string, unknown> };
		children?: React.ReactNode;
		emit: (event: string) => void;
	}>
>;

const TIER_STYLES: Record<
	string,
	{ bg: string; text: string; border: string; label: string }
> = {
	free: {
		bg: "#F3F4F6",
		text: "#6B7280",
		border: "#D1D5DB",
		label: "Free",
	},
	drama_plus: {
		bg: "#FDF2F8",
		text: "#C8A882",
		border: "#C8A882",
		label: "Drama+",
	},
	dramax: {
		bg: "#111827",
		text: "#C8A882",
		border: "#C8A882",
		label: "DramaX",
	},
};

const EMPHASIS_STYLES: Record<string, { color: string; fontWeight: number }> = {
	normal: { color: "#374151", fontWeight: 400 },
	high: { color: "#1F2937", fontWeight: 500 },
	critical: { color: "#DC2626", fontWeight: 600 },
};

const TREND_ICONS: Record<string, { icon: string; color: string }> = {
	up: { icon: "\u2191", color: "#22C55E" },
	down: { icon: "\u2193", color: "#DC2626" },
	neutral: { icon: "\u2192", color: "#6B7280" },
};

// ============================================================================
// Image component renderers
// ============================================================================

/**
 * DramaScore — large score display with colored circle
 */
function DramaScoreImage({
	element,
}: {
	element: { props: Record<string, unknown> };
	children?: React.ReactNode;
	emit: (event: string) => void;
}) {
	const score = (element.props.score as number) ?? 0;
	const category = (element.props.category as string) ?? "safe";
	const size = (element.props.size as string) ?? "md";
	const showLabel = element.props.showLabel !== false;

	const colors = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.safe;
	const circleSize = size === "lg" ? 140 : size === "sm" ? 80 : 110;
	const fontSize = size === "lg" ? 56 : size === "sm" ? 28 : 42;

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 16,
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: circleSize,
					height: circleSize,
					borderRadius: circleSize / 2,
					backgroundColor: `${colors.accent}18`,
					border: `3px solid ${colors.accent}`,
				}}
			>
				<span
					style={{
						fontSize,
						fontWeight: 800,
						color: colors.accent,
						letterSpacing: "-0.04em",
					}}
				>
					{score.toFixed(1)}
				</span>
			</div>
			{showLabel && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<span
						style={{
							fontSize: 14,
							color: "#6B7280",
							fontWeight: 500,
							textTransform: "uppercase" as const,
							letterSpacing: "0.05em",
						}}
					>
						Drama Score
					</span>
					<span
						style={{
							fontSize: 16,
							color: colors.text,
							fontWeight: 600,
						}}
					>
						/ 10
					</span>
				</div>
			)}
		</div>
	);
}

/**
 * DramaCategoryBadge — pill badge with category color
 */
function DramaCategoryBadgeImage({
	element,
}: {
	element: { props: Record<string, unknown> };
	children?: React.ReactNode;
	emit: (event: string) => void;
}) {
	const category = (element.props.category as string) ?? "safe";
	const colors = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.safe;
	const label = CATEGORY_LABELS[category] ?? "Analysis";

	return (
		<div
			style={{
				display: "flex",
				backgroundColor: colors.accent,
				color: "#FFFFFF",
				padding: "6px 16px",
				borderRadius: 20,
				fontSize: 14,
				fontWeight: 600,
			}}
		>
			{label}
		</div>
	);
}

/**
 * NarrativeBlock — text block with emphasis levels
 */
function NarrativeBlockImage({
	element,
}: {
	element: { props: Record<string, unknown> };
	children?: React.ReactNode;
	emit: (event: string) => void;
}) {
	const text = (element.props.text as string) ?? "";
	const emphasis = (element.props.emphasis as string) ?? "normal";
	const styles = EMPHASIS_STYLES[emphasis] ?? EMPHASIS_STYLES.normal;

	return (
		<div
			style={{
				display: "flex",
				fontSize: 16,
				lineHeight: 1.5,
				color: styles.color,
				fontWeight: styles.fontWeight,
			}}
		>
			{text}
		</div>
	);
}

/**
 * TierBadge — subscription tier indicator
 */
function TierBadgeImage({
	element,
}: {
	element: { props: Record<string, unknown> };
	children?: React.ReactNode;
	emit: (event: string) => void;
}) {
	const tier = (element.props.tier as string) ?? "free";
	const label = element.props.label as string | undefined;
	const config = TIER_STYLES[tier] ?? TIER_STYLES.free;
	const displayLabel = label ?? config.label;

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				backgroundColor: config.bg,
				color: config.text,
				border: `1px solid ${config.border}`,
				borderRadius: 12,
				padding: "4px 12px",
				fontSize: 12,
				fontWeight: 600,
			}}
		>
			{displayLabel}
		</div>
	);
}

/**
 * InsightRow — single insight line with optional trend indicator
 */
function InsightRowImage({
	element,
}: {
	element: { props: Record<string, unknown> };
	children?: React.ReactNode;
	emit: (event: string) => void;
}) {
	const title = (element.props.title as string) ?? "";
	const description = (element.props.description as string) ?? "";
	const trend = element.props.trend as string | undefined;
	const changePercent = element.props.changePercent as number | undefined;
	const trendCfg = trend ? TREND_ICONS[trend] : null;

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "flex-start",
				padding: "8px 0",
				width: "100%",
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
				}}
			>
				<span
					style={{
						fontSize: 14,
						fontWeight: 500,
						color: "#1F2937",
					}}
				>
					{title}
				</span>
				<span
					style={{
						fontSize: 12,
						color: "#6B7280",
						marginTop: 2,
					}}
				>
					{description.length > 80
						? description.slice(0, 77) + "..."
						: description}
				</span>
			</div>
			{(changePercent !== undefined || trendCfg) && (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 4,
					}}
				>
					{changePercent !== undefined && (
						<span
							style={{
								fontSize: 14,
								fontWeight: 500,
								color: trendCfg?.color ?? "#374151",
							}}
						>
							{changePercent > 0 ? "+" : ""}
							{changePercent}%
						</span>
					)}
					{trendCfg && (
						<span
							style={{
								fontSize: 16,
								fontWeight: 700,
								color: trendCfg.color,
							}}
						>
							{trendCfg.icon}
						</span>
					)}
				</div>
			)}
		</div>
	);
}

// ============================================================================
// Registry export
// ============================================================================

/**
 * Image component registry mapping ddCatalog names to satori-compatible renderers.
 *
 * Usage with @json-render/image:
 *   import { renderToSvg } from '@json-render/image/render';
 *   import { ddImageComponents } from '@dramadetector/ui/json-render';
 *   const svg = await renderToSvg(spec, { registry: ddImageComponents, fonts: [...] });
 */
export const ddImageComponents: ComponentRegistry = {
	DramaScore: DramaScoreImage,
	DramaCategoryBadge: DramaCategoryBadgeImage,
	NarrativeBlock: NarrativeBlockImage,
	TierBadge: TierBadgeImage,
	InsightRow: InsightRowImage,
};

// Re-export color constants for edge function use
export { CATEGORY_COLORS, CATEGORY_LABELS, TIER_STYLES };
